// Poste de garde (vigiles) : entrées et sorties des camions PBA (BL scanné et contrôlé à la sortie), livraisons fournisseurs,
// employés (pointage RH), visiteurs et véhicules. Pensé pour le téléphone ; fonctionne sans réseau (envoi au retour de la connexion).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, MenuItem,
  TextField, Typography, useMediaQuery,
} from '@mui/material';
import {
  BadgeOutlined, CancelOutlined, CheckCircleOutline, CloseOutlined, CloudOffOutlined, DirectionsCarOutlined, HistoryOutlined, Inventory2Outlined,
  LocalShippingOutlined, LoginOutlined, LogoutOutlined, PersonOutlined, QrCodeScannerOutlined, SearchOutlined, WarningAmberOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import ScannerQR from '../../components/ScannerQR';
import { gardeAPI } from '../../services/api';
import { apiError } from '../../utils/format';
import {
  demarrerSynchro, envoyerFile, executer, lireCache, lireFile, majCache, onFileChange, reessayer, supprimer,
} from '../../utils/gardeFile';

export const CAT = {
  PBA: { label: 'Camion PBA', pluriel: 'Camions PBA', icon: LocalShippingOutlined, couleur: '#12284C' },
  FOURNISSEUR: { label: 'Livraison fournisseur', pluriel: 'Fournisseurs', icon: Inventory2Outlined, couleur: '#8A5A00' },
  EMPLOYE: { label: 'Employé', pluriel: 'Employés', icon: BadgeOutlined, couleur: '#1E6B43' },
  VISITEUR: { label: 'Visiteur', pluriel: 'Visiteurs', icon: PersonOutlined, couleur: '#5B3C8C' },
  VEHICULE: { label: 'Véhicule', pluriel: 'Véhicules', icon: DirectionsCarOutlined, couleur: '#3A5070' },
};
const TYPES_VEHICULE = ['Entreprise', 'Personnel', 'Prestataire', 'Autre'];
const CHAMPS = {
  PBA: [['immatriculation', 'Immatriculation du camion', true], ['nom', 'Chauffeur'], ['societe', 'Transporteur'], ['telephone', 'Téléphone du chauffeur'], ['observations', 'Observations']],
  FOURNISSEUR: [['bc_id', 'Bon de commande'], ['societe', 'Fournisseur'], ['nature', 'Marchandise livrée (fer, ciment…)'], ['bl_fournisseur', 'N° du BL du fournisseur'],
    ['immatriculation', 'Immatriculation du camion', true], ['nom', 'Chauffeur'], ['telephone', 'Téléphone'], ['observations', 'Observations']],
  VISITEUR: [['nom', 'Nom et prénom', true], ['piece', "N° de pièce d'identité"], ['societe', 'Société / organisme'], ['telephone', 'Téléphone'],
    ['motif', 'Motif de la visite'], ['personne_visitee', 'Personne visitée'], ['immatriculation', 'Immatriculation (s\'il vient en véhicule)']],
  VEHICULE: [['immatriculation', 'Immatriculation', true], ['type_vehicule', 'Véhicule'], ['nom', 'Conducteur'], ['motif', 'Motif'], ['observations', 'Observations']],
};
const hm = (iso) => (iso ? String(iso).slice(11, 16) : '');
const duree = (iso) => {
  if (!iso) return '';
  const m = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60000));
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')}`;
};
const nomEmploye = (e) => [e.prenom, e.nom].filter(Boolean).join(' ');
// Heure du passage : l'heure actuelle par défaut, modifiable (passage noté après coup). Une heure plus tardive que maintenant = la veille.
const pad2 = (n) => String(n).padStart(2, '0');
const hhmmMaintenant = () => { const d = new Date(); return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`; };
function useHeure(actif) {
  const [h, setH] = useState(hhmmMaintenant());
  const [init, setInit] = useState(h);
  useEffect(() => { if (actif) { const x = hhmmMaintenant(); setH(x); setInit(x); } }, [actif]);
  /** Valeur envoyée : undefined = maintenant (heure non modifiée), sinon AAAA-MM-JJTHH:MM:00 */
  const le = () => {
    if (!h || h === init) return undefined;
    const d = new Date(); const [hh, mm] = h.split(':').map(Number);
    const x = new Date(d.getFullYear(), d.getMonth(), d.getDate(), hh, mm, 0);
    if (x.getTime() > Date.now() + 5 * 60000) x.setDate(x.getDate() - 1);
    return `${x.getFullYear()}-${pad2(x.getMonth() + 1)}-${pad2(x.getDate())}T${pad2(hh)}:${pad2(mm)}:00`;
  };
  return { h, setH, le, modifiee: !!h && h !== init };
}
const ChampHeure = ({ label, heure: hr, sx }) => (
  <TextField type="time" label={label} value={hr.h} onChange={(e) => hr.setH(e.target.value)} InputLabelProps={{ shrink: true }}
    inputProps={{ step: 60, style: { fontSize: 18 } }} sx={{ minWidth: 150, ...sx }}
    helperText={hr.modifiee ? 'Heure modifiée' : 'Maintenant par défaut'} />
);
/** Sortie avant l'entrée ? */
const avantEntree = (le, passage) => !!(le && passage?.entree && le < String(passage.entree).slice(0, 19));
export function titrePassage(p) {
  if (p.categorie === 'EMPLOYE') return p.employe || 'Employé';
  if (p.categorie === 'VISITEUR') return p.nom || 'Visiteur';
  return p.immatriculation || p.nom || CAT[p.categorie]?.label;
}
export function detailPassage(p) {
  const x = {
    EMPLOYE: [p.matricule, p.employe_poste],
    VISITEUR: [p.societe, p.motif, p.personne_visitee && `voit ${p.personne_visitee}`, p.immatriculation],
    PBA: [p.nom, p.societe, p.bl_numero],
    FOURNISSEUR: [p.fournisseur || p.societe, p.bc_numero && `BC ${p.bc_numero}`, p.nature, p.bl_fournisseur && `BL ${p.bl_fournisseur}`],
    VEHICULE: [p.type_vehicule, p.nom, p.motif],
  }[p.categorie] || [];
  return x.filter(Boolean).join(' · ');
}

// ---------------------------------------------------------------------
// Entrée (camion PBA, fournisseur, visiteur, véhicule)
// ---------------------------------------------------------------------
function EntreeDialog({ categorie, bcs, onClose, onFait }) {
  const [f, setF] = useState({});
  const hr = useHeure(categorie);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF(categorie === 'VEHICULE' ? { type_vehicule: 'Entreprise' } : {}); setError(''); }, [categorie]);
  if (!categorie) return null;
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: k === 'immatriculation' ? e.target.value.toUpperCase() : e.target.value }));
  const choisirBc = (e) => { const b = bcs.find((x) => x.id === e.target.value); setF((x) => ({ ...x, bc_id: e.target.value, societe: b?.fournisseur || x.societe, nature: x.nature || b?.articles || '' })); };
  const valider = async () => {
    const manque = CHAMPS[categorie].find(([k, , req]) => req && !String(f[k] || '').trim());
    if (manque) { setError(`${manque[1]} : à renseigner`); return; }
    setBusy(true); setError('');
    try {
      const r = await executer('entree', { categorie, ...f, bc_id: f.bc_id || null, le: hr.le() }, `Entrée ${CAT[categorie].label} ${f.immatriculation || f.nom || ''}`.trim());
      onFait(r.horsLigne ? 'Pas de réseau : entrée gardée sur le téléphone, envoyée au retour de la connexion' : r.data.message);
    } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" fullScreen={window.innerWidth < 600}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><LoginOutlined /> Entrée — {CAT[categorie].label}
        <IconButton sx={{ ml: 'auto' }} onClick={onClose} aria-label="Fermer"><CloseOutlined /></IconButton></DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <ChampHeure label="Heure d'entrée" heure={hr} sx={{ mb: 2 }} />
        {CHAMPS[categorie].map(([k, label, req]) => {
          if (k === 'bc_id') {
            return (
              <TextField key={k} select fullWidth label={label} value={f.bc_id || ''} onChange={choisirBc} sx={{ mb: 2 }}
                helperText={bcs.length ? 'Commande attendue (facultatif) : les achats sont prévenus de l\'arrivée' : 'Aucune commande attendue connue'}>
                <MenuItem value="">— Sans bon de commande —</MenuItem>
                {bcs.map((b) => <MenuItem key={b.id} value={b.id}>{b.numero} · {b.fournisseur}{b.date_prevue ? ` · prévu le ${b.date_prevue.split('-').reverse().join('/')}` : ''}</MenuItem>)}
              </TextField>
            );
          }
          if (k === 'type_vehicule') {
            return <TextField key={k} select fullWidth label={label} value={f[k] || ''} onChange={set(k)} sx={{ mb: 2 }}>{TYPES_VEHICULE.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}</TextField>;
          }
          return (
            <TextField key={k} fullWidth label={`${label}${req ? ' *' : ''}`} value={f[k] || ''} onChange={set(k)} sx={{ mb: 2 }}
              multiline={k === 'observations'} minRows={k === 'observations' ? 2 : undefined}
              inputProps={{ autoCapitalize: k === 'immatriculation' ? 'characters' : 'sentences', inputMode: k === 'telephone' ? 'tel' : 'text' }} />
          );
        })}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="contained" size="large" startIcon={<LoginOutlined />} disabled={busy} onClick={valider}>Enregistrer l'entrée</Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------
// Employés : une touche pour l'entrée ou la sortie
// ---------------------------------------------------------------------
function EmployesDialog({ open, employes, surSite, onClose, onFait }) {
  const [q, setQ] = useState('');
  const hr = useHeure(open);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { if (open) { setQ(''); setError(''); } }, [open]);
  const presents = useMemo(() => Object.fromEntries(surSite.filter((p) => p.categorie === 'EMPLOYE').map((p) => [p.employe_id, p])), [surSite]);
  const liste = useMemo(() => {
    const n = q.trim().toLowerCase();
    return employes.filter((e) => !n || `${e.nom} ${e.prenom} ${e.matricule} ${e.badge || ''} ${e.poste || ''}`.toLowerCase().includes(n));
  }, [employes, q]);
  if (!open) return null;
  const agir = async (e) => {
    const p = presents[e.id];
    const le = hr.le();
    if (p && avantEntree(le, p)) { setError(`${nomEmploye(e)} : l'heure de sortie ne peut pas précéder l'entrée (${hm(p.entree)})`); return; }
    setBusy(e.id); setError('');
    try {
      const r = p ? await executer('sortie', { ref: p.uid || p.id, le }, `Sortie ${nomEmploye(e)}`)
        : await executer('entree', { categorie: 'EMPLOYE', employe_id: e.id, le }, `Entrée ${nomEmploye(e)}`);
      onFait(`${nomEmploye(e)} : ${p ? 'sortie' : 'entrée'} enregistrée${r.horsLigne ? ' (sur le téléphone, envoi au retour du réseau)' : ''}`, true);
    } catch (x) { setError(apiError(x)); } finally { setBusy(null); }
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" fullScreen={window.innerWidth < 600}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><BadgeOutlined /> Employés
        <IconButton sx={{ ml: 'auto' }} onClick={onClose} aria-label="Fermer"><CloseOutlined /></IconButton></DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ p: 2, pb: 1, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
          {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
          <TextField fullWidth autoFocus placeholder="Nom, matricule ou badge" value={q} onChange={(e) => setQ(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchOutlined /></InputAdornment> }} />
          <ChampHeure label="Heure d'entrée / de sortie" heure={hr} sx={{ mt: 1.5, width: '100%' }} />
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.75 }}>{Object.keys(presents).length} employé(s) sur le site · l'entrée et la sortie remplissent le pointage</Typography>
        </Box>
        {liste.map((e) => {
          const p = presents[e.id];
          return (
            <Box key={e.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, borderTop: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600 }} noWrap>{nomEmploye(e)}</Typography>
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }} noWrap>
                  {[e.matricule, e.poste].filter(Boolean).join(' · ')}{p ? ` · sur le site depuis ${hm(p.entree)}` : ''}
                </Typography>
              </Box>
              <Button variant={p ? 'outlined' : 'contained'} color={p ? 'warning' : 'success'} disabled={busy === e.id} onClick={() => agir(e)}
                startIcon={p ? <LogoutOutlined /> : <LoginOutlined />} sx={{ minWidth: 112 }}>
                {p ? 'Sortie' : 'Entrée'}
              </Button>
            </Box>
          );
        })}
        {!liste.length && <Typography sx={{ p: 3, color: 'text.secondary', textAlign: 'center' }}>{employes.length ? 'Aucun employé trouvé' : 'Liste des employés indisponible (fiches RH)'}</Typography>}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------
// Sortie d'un camion PBA : scan du BL, immatriculation, comptage des poteaux
// ---------------------------------------------------------------------
const COULEUR_RES = { AUTORISEE: ['#1E6B43', '#E2F1E8'], ECART: ['#9A5B00', '#FBF0DA'], REFUSEE: ['#B4232A', '#F8E1E2'], NON_CONTROLE: ['#9A5B00', '#FBF0DA'] };
function SortiePbaDialog({ open, passage, onClose, onFait }) {
  const [etape, setEtape] = useState('scan');
  const hr = useHeure(open);
  const [scanner, setScanner] = useState(true);
  const [numero, setNumero] = useState('');
  const [bl, setBl] = useState(null);
  const [immat, setImmat] = useState('');
  const [comptes, setComptes] = useState({});
  const [obs, setObs] = useState('');
  const [res, setRes] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const enLigne = navigator.onLine;
  useEffect(() => {
    if (!open) return;
    setEtape('scan'); setScanner(true); setNumero(''); setBl(null); setImmat(passage?.immatriculation || ''); setComptes({}); setObs(''); setRes(null); setError('');
  }, [open, passage]);
  const verifier = useCallback(async (valeur) => {
    const v = String(valeur || '').trim();
    if (!v) { setError('Scannez le QR code ou tapez le numéro du BL'); return; }
    setBusy(true); setError(''); setNumero(v);
    try {
      const r = await gardeAPI.bl(v);
      setBl(r.data); setEtape('saisie');
    } catch (e) {
      if (!e.response) { setError('Pas de réseau : le BL ne peut pas être vérifié.'); setScanner(false); } else setError(apiError(e));
    } finally { setBusy(false); }
  }, []);
  const onScan = useCallback((t) => { setScanner(false); verifier(t); }, [verifier]);
  if (!open) return null;
  const bloquant = bl && (bl.inconnu || bl.statut === 'ANNULEE' || !bl.scelle || bl.deja_sorti);
  const valider = async () => {
    const le = hr.le();
    if (avantEntree(le, passage)) { setError(`L'heure de sortie ne peut pas précéder l'entrée du camion (${hm(passage.entree)})`); return; }
    setBusy(true); setError('');
    try {
      const r = await executer('pba', { numero: bl?.numero || numero, ref: passage ? (passage.uid || passage.id) : null, immatriculation: immat, comptes, observations: obs, le }, `Sortie camion PBA ${bl?.numero || numero}`);
      if (r.horsLigne) { onFait('Pas de réseau : sortie gardée sur le téléphone ; le contrôle sera fait à l\'envoi', true); onClose(); return; }
      setRes(r.data); setEtape('resultat'); onFait(null, false);
    } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };
  const nonControlee = async () => {
    if (!immat.trim()) { setError('Immatriculation du camion requise'); return; }
    setBusy(true); setError('');
    try {
      await executer('pbaNC', { numero, ref: passage ? (passage.uid || passage.id) : null, immatriculation: immat, observations: obs, le: hr.le() }, `Sortie camion PBA non contrôlée ${immat}`);
      onFait('Sortie non contrôlée enregistrée : les responsables seront prévenus', true); onClose();
    } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };
  const sansChargement = async () => {
    if (!passage) return;
    if (!window.confirm('Le camion sort vide, sans poteaux ? (aucun BL)')) return;
    setBusy(true);
    try { await executer('sortie', { ref: passage.uid || passage.id, sans_livraison: true, observations: obs || 'Sorti sans chargement', le: hr.le() }, `Sortie à vide ${passage.immatriculation || ''}`); onFait('Sortie à vide enregistrée', true); onClose(); } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };
  const [fg, bg] = res ? COULEUR_RES[res.resultat] : [];
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" fullScreen={window.innerWidth < 600}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><QrCodeScannerOutlined /> Sortie camion PBA{passage?.immatriculation ? ` — ${passage.immatriculation}` : ''}
        <IconButton sx={{ ml: 'auto' }} onClick={onClose} aria-label="Fermer"><CloseOutlined /></IconButton></DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {etape === 'scan' && (
          <>
            {enLigne && scanner && <ScannerQR onResultat={onScan} onFermer={() => setScanner(false)} />}
            {(!enLigne || !scanner) && (
              <>
                {!enLigne && <Alert severity="warning" icon={<CloudOffOutlined />} sx={{ mb: 2 }}>Pas de réseau : le BL ne peut pas être vérifié. Si le camion doit sortir, enregistrez une sortie <b>non contrôlée</b> : les responsables seront prévenus.</Alert>}
                <TextField fullWidth label="N° du BL (ex. BL-2026-000523 ou 523)" value={numero} onChange={(e) => setNumero(e.target.value)} sx={{ mb: 2 }}
                  inputProps={{ autoCapitalize: 'characters' }} onKeyDown={(e) => { if (e.key === 'Enter' && enLigne) verifier(numero); }} />
                {enLigne && <Button fullWidth onClick={() => setScanner(true)} startIcon={<QrCodeScannerOutlined />}>Scanner le QR code</Button>}
                {!enLigne && <TextField fullWidth label="Immatriculation du camion *" value={immat} onChange={(e) => setImmat(e.target.value.toUpperCase())} sx={{ mb: 2 }} />}
                {!enLigne && <ChampHeure label="Heure de sortie" heure={hr} sx={{ mb: 2 }} />}
                {!enLigne && <TextField fullWidth label="Observations" value={obs} onChange={(e) => setObs(e.target.value)} multiline minRows={2} />}
              </>
            )}
            {busy && <Box sx={{ textAlign: 'center', mt: 2 }}><CircularProgress size={28} /></Box>}
          </>
        )}
        {etape === 'saisie' && bl && (
          <>
            {bloquant && (
              <Alert severity="error" icon={<CancelOutlined />} sx={{ mb: 2, fontWeight: 600 }}>
                NE PAS LAISSER SORTIR — {bl.inconnu ? `BL ${bl.numero} inconnu` : bl.statut === 'ANNULEE' ? 'BL annulé' : !bl.scelle ? 'BL non scellé (validations incomplètes)' : `BL déjà utilisé le ${String(bl.deja_sorti.sortie).slice(8, 10)}/${String(bl.deja_sorti.sortie).slice(5, 7)} à ${hm(bl.deja_sorti.sortie)}`}.
                {' '}Enregistrez pour prévenir les responsables.
              </Alert>
            )}
            {!bl.inconnu && (
              <Box sx={{ p: 1.5, mb: 2, borderRadius: 1.5, bgcolor: 'action.hover' }}>
                <Typography sx={{ fontWeight: 700 }}>{bl.numero}{bl.scelle && <Chip size="small" color="success" label={`Scellé · ${bl.code}`} sx={{ ml: 1 }} />}</Typography>
                <Typography sx={{ fontSize: 14 }}>{bl.client}{bl.destination ? ` → ${bl.destination}` : ''}</Typography>
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{[bl.transporteur, bl.chauffeur, bl.immatriculation && `immat. ${bl.immatriculation}`].filter(Boolean).join(' · ')}</Typography>
              </Box>
            )}
            {!bloquant && (
              <>
                <TextField fullWidth label="Immatriculation relevée sur le camion *" value={immat} onChange={(e) => setImmat(e.target.value.toUpperCase())} sx={{ mb: 2 }}
                  helperText={bl.immatriculation ? `Sur le BL : ${bl.immatriculation}` : 'Le BL ne précise pas l\'immatriculation'} inputProps={{ autoCapitalize: 'characters' }} />
                <Typography sx={{ fontWeight: 600, mb: 1 }}>Poteaux comptés sur le camion</Typography>
                {bl.lignes.map((l) => (
                  <Box key={l.pba_type} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.25 }}>
                    <Box sx={{ flex: 1 }}><Typography sx={{ fontWeight: 600 }}>{l.pba_type}</Typography><Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{l.quantite} sur le BL</Typography></Box>
                    <TextField size="small" sx={{ width: 110 }} value={comptes[l.pba_type] ?? ''} placeholder="Compté"
                      onChange={(e) => setComptes((c) => ({ ...c, [l.pba_type]: e.target.value.replace(/\D/g, '') }))}
                      inputProps={{ inputMode: 'numeric', style: { textAlign: 'center', fontSize: 18 }, 'aria-label': `Poteaux ${l.pba_type} comptés` }} />
                  </Box>
                ))}
              </>
            )}
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 1 }}>
              {passage?.entree && <TextField label="Heure d'entrée" value={hm(passage.entree)} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={{ width: 150 }} helperText="Notée à l'entrée" />}
              <ChampHeure label="Heure de sortie" heure={hr} />
            </Box>
            <TextField fullWidth label="Observations" value={obs} onChange={(e) => setObs(e.target.value)} multiline minRows={2} sx={{ mt: 1 }} />
          </>
        )}
        {etape === 'resultat' && res && (
          <Box sx={{ textAlign: 'center', p: 3, borderRadius: 2, bgcolor: bg, color: fg }}>
            {res.resultat === 'AUTORISEE' ? <CheckCircleOutline sx={{ fontSize: 72 }} /> : res.resultat === 'REFUSEE' ? <CancelOutlined sx={{ fontSize: 72 }} /> : <WarningAmberOutlined sx={{ fontSize: 72 }} />}
            <Typography sx={{ fontSize: 26, fontWeight: 800, mt: 1 }}>{{ AUTORISEE: 'SORTIE AUTORISÉE', ECART: 'SORTIE AVEC ÉCART', REFUSEE: 'NE PAS LAISSER SORTIR' }[res.resultat]}</Typography>
            {res.resultat !== 'AUTORISEE' && <Typography sx={{ mt: 1 }}>{res.message}</Typography>}
            {res.anomalies?.length > 0 && <Box component="ul" sx={{ textAlign: 'left', mt: 2, mb: 0 }}>{res.anomalies.map((a) => <li key={a}>{a}</li>)}</Box>}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, flexWrap: 'wrap', gap: 1 }}>
        {etape === 'scan' && passage && <Button color="inherit" onClick={sansChargement} disabled={busy}>Sorti à vide</Button>}
        {etape === 'scan' && !enLigne && <Button variant="contained" color="warning" disabled={busy} onClick={nonControlee}>Sortie non contrôlée</Button>}
        {etape === 'scan' && enLigne && !scanner && <Button variant="contained" disabled={busy} onClick={() => verifier(numero)}>Vérifier le BL</Button>}
        {etape === 'saisie' && <Button onClick={() => { setEtape('scan'); setScanner(true); setBl(null); }}>Autre BL</Button>}
        {etape === 'saisie' && <Button variant="contained" size="large" color={bloquant ? 'error' : 'primary'} disabled={busy} onClick={valider}>{bloquant ? 'Enregistrer le refus' : 'Valider la sortie'}</Button>}
        {etape === 'resultat' && <Button variant="contained" size="large" onClick={onClose}>Terminer</Button>}
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------
// Sortie simple (visiteur, véhicule, fournisseur)
// ---------------------------------------------------------------------
function SortieDialog({ passage, onClose, onFait }) {
  const [obs, setObs] = useState('');
  const hr = useHeure(passage);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { setObs(''); setError(''); }, [passage]);
  if (!passage) return null;
  const valider = async () => {
    setBusy(true); setError('');
    try {
      const le = hr.le();
      if (avantEntree(le, passage)) { setError(`L'heure de sortie ne peut pas précéder l'entrée (${hm(passage.entree)})`); setBusy(false); return; }
      const r = await executer('sortie', { ref: passage.uid || passage.id, observations: obs, le }, `Sortie ${titrePassage(passage)}`);
      onFait(r.horsLigne ? 'Pas de réseau : sortie gardée sur le téléphone' : r.data.message, true); onClose();
    } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Sortie — {titrePassage(passage)}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography sx={{ fontSize: 14, mb: 2 }}>{CAT[passage.categorie].label}{detailPassage(passage) ? ` · ${detailPassage(passage)}` : ''}<br />Entré à {hm(passage.entree)} ({duree(passage.entree)})</Typography>
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
          <TextField label="Heure d'entrée" value={hm(passage.entree) || '—'} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
          <ChampHeure label="Heure de sortie" heure={hr} />
        </Box>
        <TextField fullWidth label="Observations (facultatif)" value={obs} onChange={(e) => setObs(e.target.value)} multiline minRows={2} />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="contained" size="large" startIcon={<LogoutOutlined />} disabled={busy} onClick={valider}>Enregistrer la sortie</Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------
const Garde = () => {
  const navigate = useNavigate();
  const etroit = useMediaQuery('(max-width:600px)');
  const cache = lireCache();
  const [etat, setEtat] = useState(cache.etat || null);
  const [employes, setEmployes] = useState(cache.employes || []);
  const [bcs, setBcs] = useState(cache.bcs || []);
  const [file, setFile] = useState(lireFile());
  const [enLigne, setEnLigne] = useState(navigator.onLine);
  const [info, setInfo] = useState('');
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [entree, setEntree] = useState(null);
  const [emp, setEmp] = useState(false);
  const [pba, setPba] = useState(null); // { passage } ou {}
  const [sortie, setSortie] = useState(null);
  const [voirFile, setVoirFile] = useState(false);

  const charger = useCallback(async () => {
    try {
      const [e, em, b] = await Promise.all([gardeAPI.etat(), gardeAPI.employes(), gardeAPI.bcAttendus()]);
      setEtat(e.data); setEmployes(em.data); setBcs(b.data); setError('');
      majCache({ etat: e.data, employes: em.data, bcs: b.data });
    } catch (x) { if (x.response) setError(apiError(x)); }
  }, []);
  useEffect(() => {
    charger();
    demarrerSynchro(() => charger());
    const maj = () => setFile(lireFile());
    const on = () => { setEnLigne(true); charger(); }; const off = () => setEnLigne(false);
    window.addEventListener('online', on); window.addEventListener('offline', off);
    const stop = onFileChange(maj);
    const t = setInterval(() => { if (navigator.onLine) charger(); }, 60000);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); stop(); clearInterval(t); };
  }, [charger]);

  // Liste « sur le site » : celle du serveur, plus les entrées et moins les sorties encore sur le téléphone
  const surSite = useMemo(() => {
    const base = etat?.sur_site || [];
    const sorties = new Set(file.filter((a) => ['sortie', 'pba', 'pbaNC'].includes(a.type) && a.payload.ref).map((a) => String(a.payload.ref)));
    const locaux = file.filter((a) => a.type === 'entree' && !a.erreur).map((a) => {
      const e = employes.find((x) => x.id === Number(a.payload.employe_id));
      return { ...a.payload, id: null, uid: a.payload.uid, entree: a.payload.le, employe: e ? nomEmploye(e) : null, matricule: e?.matricule, local: true };
    });
    return [...locaux, ...base].filter((p) => !sorties.has(String(p.uid)) && !sorties.has(String(p.id)));
  }, [etat, file, employes]);
  const visibles = useMemo(() => {
    const n = q.trim().toLowerCase();
    return surSite.filter((p) => !n || `${titrePassage(p)} ${detailPassage(p)} ${p.immatriculation || ''}`.toLowerCase().includes(n));
  }, [surSite, q]);
  const compte = (c) => surSite.filter((p) => p.categorie === c).length;
  const fait = (m, recharger = true) => { if (m) setInfo(m); if (recharger && navigator.onLine) charger(); };

  const Bouton = ({ icon: Icon, titre, sous, couleur, onClick }) => (
    <Button onClick={onClick} variant="outlined" sx={{ p: 1.5, minHeight: 84, justifyContent: 'flex-start', textAlign: 'left', gap: 1.25, borderColor: 'divider', bgcolor: 'background.paper', color: 'text.primary', '&:hover': { borderColor: couleur } }}>
      <Box sx={{ width: 42, height: 42, borderRadius: 1.5, display: 'grid', placeItems: 'center', bgcolor: couleur, color: '#fff', flexShrink: 0 }}><Icon /></Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 15, lineHeight: 1.2 }}>{titre}</Typography>
        {sous && <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.3, mt: 0.25 }}>{sous}</Typography>}
      </Box>
    </Button>
  );
  const refuses = file.filter((a) => a.erreur);

  return (
    <Layout title="Poste de garde" plain
      actions={<Button startIcon={<HistoryOutlined />} onClick={() => navigate('/garde/journal')}>Journal</Button>}>
      {(!enLigne || file.length > 0) && (
        <Alert severity={!enLigne ? 'warning' : refuses.length ? 'error' : 'info'} icon={!enLigne ? <CloudOffOutlined /> : undefined} sx={{ mb: 2 }}
          action={file.length > 0 && <>{enLigne && <Button color="inherit" size="small" onClick={async () => { const r = await envoyerFile(); if (r?.envoyes) fait(`${r.envoyes} passage(s) envoyé(s)`); }}>Envoyer</Button>}
            <Button color="inherit" size="small" onClick={() => setVoirFile(true)}>Voir</Button></>}>
          {!enLigne && 'Hors connexion : continuez à saisir, tout sera envoyé au retour du réseau. '}
          {file.length > 0 && `${file.length} passage(s) en attente sur ce téléphone${refuses.length ? `, dont ${refuses.length} refusé(s)` : ''}.`}
        </Alert>
      )}
      {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo('')}>{info}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        {Object.entries(CAT).map(([c, v]) => <Chip key={c} icon={<v.icon />} label={`${v.pluriel} : ${compte(c)}`} variant={compte(c) ? 'filled' : 'outlined'} />)}
        {etat?.aujourdhui?.alertes > 0 && <Chip color="error" icon={<WarningAmberOutlined />} label={`${etat.aujourdhui.alertes} alerte(s) aujourd'hui`} onClick={() => navigate('/garde/journal?alertes=1')} />}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: etroit ? '1fr' : 'repeat(3, 1fr)', gap: 1.25, mb: 3 }}>
        <Bouton icon={LocalShippingOutlined} couleur={CAT.PBA.couleur} titre="Camion PBA — Entrée" sous="Avant le chargement" onClick={() => setEntree('PBA')} />
        <Bouton icon={QrCodeScannerOutlined} couleur="#B4232A" titre="Camion PBA — Sortie" sous="Scanner le BL, compter les poteaux" onClick={() => setPba({})} />
        <Bouton icon={Inventory2Outlined} couleur={CAT.FOURNISSEUR.couleur} titre="Livraison fournisseur" sous="Fer, ciment, béton, sable…" onClick={() => setEntree('FOURNISSEUR')} />
        <Bouton icon={BadgeOutlined} couleur={CAT.EMPLOYE.couleur} titre="Employés" sous="Entrée / sortie (pointage)" onClick={() => setEmp(true)} />
        <Bouton icon={PersonOutlined} couleur={CAT.VISITEUR.couleur} titre="Visiteur" sous="Nom, pièce, motif" onClick={() => setEntree('VISITEUR')} />
        <Bouton icon={DirectionsCarOutlined} couleur={CAT.VEHICULE.couleur} titre="Véhicule" sous="Entreprise, personnel, prestataire" onClick={() => setEntree('VEHICULE')} />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontWeight: 700, fontSize: 17, flex: 1 }}>Sur le site ({surSite.filter((p) => p.categorie !== 'EMPLOYE').length} hors employés)</Typography>
        <TextField size="small" placeholder="Rechercher" value={q} onChange={(e) => setQ(e.target.value)} sx={{ width: etroit ? '100%' : 260 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchOutlined fontSize="small" /></InputAdornment> }} />
      </Box>
      {!etat && enLigne && <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={28} /></Box>}
      {visibles.filter((p) => p.categorie !== 'EMPLOYE' || q).map((p) => {
        const C = CAT[p.categorie];
        return (
          <Box key={p.uid || p.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: 1.25, mb: 1, borderRadius: 1.5, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ width: 38, height: 38, borderRadius: 1.25, display: 'grid', placeItems: 'center', bgcolor: C.couleur, color: '#fff', flexShrink: 0 }}><C.icon fontSize="small" /></Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700 }} noWrap>{titrePassage(p)}{p.local && <Chip size="small" label="sur le téléphone" sx={{ ml: 1, height: 20 }} />}</Typography>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }} noWrap>{C.label}{detailPassage(p) ? ` · ${detailPassage(p)}` : ''}</Typography>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Entré à {hm(p.entree)} · {duree(p.entree)}</Typography>
            </Box>
            <Button variant="contained" color={p.categorie === 'PBA' ? 'error' : 'primary'} startIcon={p.categorie === 'PBA' ? <QrCodeScannerOutlined /> : <LogoutOutlined />}
              onClick={() => (p.categorie === 'PBA' ? setPba({ passage: p }) : p.categorie === 'EMPLOYE' ? setEmp(true) : setSortie(p))}>Sortie</Button>
          </Box>
        );
      })}
      {etat && !visibles.filter((p) => p.categorie !== 'EMPLOYE' || q).length && <Typography sx={{ color: 'text.secondary', py: 2, textAlign: 'center' }}>{q ? 'Aucun résultat' : 'Aucun véhicule ni visiteur sur le site'}</Typography>}

      <EntreeDialog categorie={entree} bcs={bcs} onClose={() => setEntree(null)} onFait={(m) => { setEntree(null); fait(m); }} />
      <EmployesDialog open={emp} employes={employes} surSite={surSite} onClose={() => setEmp(false)} onFait={fait} />
      <SortiePbaDialog open={!!pba} passage={pba?.passage || null} onClose={() => { setPba(null); charger(); }} onFait={fait} />
      <SortieDialog passage={sortie} onClose={() => setSortie(null)} onFait={fait} />
      <Dialog open={voirFile} onClose={() => setVoirFile(false)} fullWidth maxWidth="sm">
        <DialogTitle>Passages en attente sur ce téléphone</DialogTitle>
        <DialogContent dividers>
          {file.map((a) => (
            <Box key={a.payload.uid} sx={{ mb: 1.5, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography sx={{ fontWeight: 600 }}>{a.libelle}</Typography>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{String(a.payload.le).replace('T', ' à ').slice(0, 18)}</Typography>
              {a.erreur && <Alert severity="error" sx={{ mt: 1 }}>{a.erreur}</Alert>}
              <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                {a.erreur && <Button size="small" onClick={() => reessayer(a.payload.uid)}>Réessayer</Button>}
                <Button size="small" color="error" onClick={() => { if (window.confirm('Supprimer ce passage du téléphone ? Il ne sera pas envoyé.')) supprimer(a.payload.uid); }}>Supprimer</Button>
              </Box>
            </Box>
          ))}
          {!file.length && <Typography sx={{ color: 'text.secondary' }}>Rien en attente.</Typography>}
        </DialogContent>
        <DialogActions><Button onClick={() => setVoirFile(false)}>Fermer</Button></DialogActions>
      </Dialog>
    </Layout>
  );
};

export default Garde;
